import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "emailjs-com";
import BasePage from "../components/BasePage";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";
import Hero from "../components/Hero";

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    emailjs
      .send(
        "service_ofkkhte",
        "template_zyet93w",
        formData,
        "f6D4NPBwAzH6tonEy"
      )
      .then(() => {
        setFormData({ name: "", email: "", message: "" });
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 4000);
      })
      .catch((error) => {
        console.error("EmailJS error:", error);
        alert("An error occurred while sending your message.");
      })
      .finally(() => setIsLoading(false));
  };

  const inputAnimation = {
    initial: { borderColor: "transparent" },
    animate: { borderColor: "var(--color-primary)" },
    transition: { duration: 0.3 },
  };

  return (
    <BasePage
      title="Contact Us - UcWaves"
      description="Get in touch with UcWaves for inquiries or support. Fill out our contact form to connect with us."
      keywords="Contact, UcWaves, support, inquiry"
    >
      {/* Hero Section */}
      <div className="relative">
        <Hero />
      </div>

      <main>
        <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
          {/* Decorative images */}
          <img
            src={imgL}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute left-4 md:left-16 xl:left-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          <img
            src={imgR}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />

          {/* Section title */}
          <div className="relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <h1 className="text-6xl md:text-[64px] font-bold md:mt-15 text-secondary-light text-center mb-32">
                UnCensored Waves <br />
                Contact Us
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Form centered */}
        <div className="flex justify-center pb-20 px-4">
          <div className="bg-[var(--color-primary-bg)]/90 backdrop-blur-sm shadow-2xl p-8 w-full max-w-lg rounded-lg">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Name */}
              <label
                htmlFor="name"
                className="text-lg font-semibold text-[var(--color-text-primary)] px-3 pt-3"
              >
                Name:
              </label>
              <motion.div
                className="flex flex-col border-2 rounded-md"
                initial="initial"
                animate={focusedField === "name" ? "animate" : "initial"}
                variants={inputAnimation}
              >
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("name")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="p-3 bg-white/80 text-[var(--color-text-dark)] focus:outline-none rounded-md"
                />
              </motion.div>

              {/* Email */}
              <label
                htmlFor="email"
                className="text-lg font-semibold text-[var(--color-text-primary)] px-3 pt-3"
              >
                Email:
              </label>
              <motion.div
                className="flex flex-col border-2 rounded-md"
                initial="initial"
                animate={focusedField === "email" ? "animate" : "initial"}
                variants={inputAnimation}
              >
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="p-3 bg-white/80 text-[var(--color-text-dark)] focus:outline-none rounded-md"
                />
              </motion.div>

              {/* Message */}
              <label
                htmlFor="message"
                className="text-lg font-semibold text-[var(--color-text-primary)] px-3 pt-3"
              >
                Message:
              </label>
              <motion.div
                className="flex flex-col border-2 rounded-md"
                initial="initial"
                animate={focusedField === "message" ? "animate" : "initial"}
                variants={inputAnimation}
              >
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("message")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="p-3 bg-white/80 text-[var(--color-text-dark)] resize-none min-h-[8rem] focus:outline-none rounded-md"
                />
              </motion.div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                className={`py-3 font-semibold text-lg text-white rounded transition ${
                  isLoading
                    ? "bg-[var(--color-primary)] opacity-60 cursor-not-allowed"
                    : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-light)]"
                }`}
                whileTap={{ scale: 0.95 }}
              >
                {isLoading ? "Sending..." : "Submit"}
              </motion.button>

              {/* Success Message */}
              <AnimatePresence>
                {isSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-green-600 text-center font-medium"
                  >
                    ✅ Your message has been sent successfully!
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>
      </main>
    </BasePage>
  );
};

export default ContactPage;
